import * as React from 'react';
import {
  Accordion as MatExpansionPanel,
  accordionClasses,
  AccordionDetails as MatExpansionPanelDetails,
  AccordionSummary as MatExpansionPanelSummary,
  accordionSummaryClasses,
  buttonBaseClasses,
  paperClasses,
  SxProps,
  Theme,
  typographyClasses
} from '@mui/material';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { keygen, theme } from '../../utils';
import { ButtonBlueprint, createButton } from '../../factories/button';
import { LayoutGrid } from '../LayoutGrid';

const styles: Record<string, SxProps<Theme>> = {
  root: {
    marginTop: 0,
    marginBottom: 2,
    [`&.${accordionClasses['root']}`]: {},
    [`&.${accordionClasses['expanded']}`]: {
      marginTop: 0,
      marginBottom: 2,
      minHeight: '1.875rem'
    },
    [`&.${paperClasses['elevation']}`]: {
      boxShadow: 'none'
    },
    [`& .${typographyClasses['root']}`]: {
      marginRight: '6px'
    },
    [`& .${accordionSummaryClasses['root']}`]: {
      minHeight: '5px'
    },
    [`& .${buttonBaseClasses['root']}`]: {
      alignItems: 'center',
      marginTop: '2px'
    }
  },
  infoClass: {
    order: 2
  },
  customActionsWrapper: {
    order: 2,
    display: 'flex',
    marginLeft: 1,
    '& svg': {
      fill: 'common.white'
    },
    [`& .${buttonBaseClasses['root']}`]: {
      '&:hover': {
        background: 'rgba(255,255,255,0.07)'
      },
      '&:active, &:focus': {
        background: 'rgba(255,255,255,0.15)'
      }
    }
  },
  content: {
    backgroundColor: 'text.secondary',
    color: 'common.white',
    paddingLeft: 0,
    height: 'auto',
    [`& .${accordionSummaryClasses['content']}.${accordionSummaryClasses['contentGutters']}`]: {
      alignItems: 'center',
      margin: '4px 0'
    },
    textTransform: theme.typography.button.textTransform,
    '& :first-child': {
      order: 2,
      alignItems: 'flex-start'
    },
    [`&.${accordionClasses['expanded']}`]: {
      minHeight: '1.875rem'
    },
    [`&.${accordionSummaryClasses['focusVisible']}`]: {
      backgroundColor: 'rgba(97, 97, 97, 0.75)'
    },
    '& [class*="MuiIconButton"]': {
      fontSize: '1.25rem',
      margin: 0,
      padding: 0,
      color: 'common.white'
    },
    '& [icon*="InfoOutlined"]': {
      margin: '0 0.5rem'
    },
    '& [class*="expandIcon"]': {
      order: 1,
      color: 'common.white',
      left: 0,
      right: 'auto',
      paddingLeft: 0,
      '& :last-child': {
        padding: 0
      },
      '&[class*="MuiButtonBase"]': {
        padding: 0
      },
      transform: 'rotate(-90deg)',
      [`&.${accordionClasses['expanded']}`]: {
        transform: 'rotate(0deg)'
      }
    }
  },
  basicContent: {
    color: 'text.primary',
    height: 'auto',
    '& [class*="expandIcon"]': {
      color: 'text.primary'
    }
  }
};

export interface ExpansionPanelProps {
  dataProp?: string;
  disabled?: boolean;
  defaultExpanded?: boolean;
  children?: React.ReactNode;
  expanded?: boolean;
  square?: boolean;
  title: string;
  changeHandler?: Function;
  infoTooltipText?: string;
  headerActions?: ButtonBlueprint[];
  showBasicExpansionPanel?: boolean;
  sx?: SxProps<Theme>;
}

const handleChange = (handler?: Function) => () => {
  if (handler) {
    handler();
  }
};

const ExpansionPanel: React.FunctionComponent<ExpansionPanelProps> = ({
  children,
  dataProp,
  title,
  infoTooltipText,
  defaultExpanded,
  expanded,
  disabled,
  changeHandler,
  square,
  headerActions,
  showBasicExpansionPanel,
  sx
}) => {
  const { basicContent, content, root, infoClass, customActionsWrapper } = styles;
  const expansionPanelId = keygen();
  return (
    <MatExpansionPanel
      sx={[root, sx] as SxProps<Theme>}
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      disabled={disabled}
      onChange={handleChange(changeHandler)}
      square={square}
      data-selector={dataProp}
    >
      {
        <MatExpansionPanelSummary
          sx={showBasicExpansionPanel ? basicContent : content}
          expandIcon={showBasicExpansionPanel ? <Icon icon="ExpandMore" /> : <Icon icon="ArrowDropDown" />}
          id={`Expansion-Panel-Header-${expansionPanelId}`}
          aria-controls={`Expansion-Panel-Content-${expansionPanelId}`}
        >
          <Typography variant="h4">{title}</Typography>
          {infoTooltipText && !headerActions ? (
            <Tooltip aria-label={infoTooltipText} sx={infoClass} title={infoTooltipText}>
              <IconButton icon="InfoOutlined" fontSize="small" isExpansionPanelIcon={true} actionType="INFO" />
            </Tooltip>
          ) : null}

          {headerActions ? (
            <LayoutGrid sx={customActionsWrapper}>
              {headerActions?.map((action: ButtonBlueprint) =>
                createButton({
                  ...action,
                  onClick: e => {
                    e.stopPropagation();
                    action.onClick && action.onClick(e);
                  }
                })
              )}
            </LayoutGrid>
          ) : null}
        </MatExpansionPanelSummary>
      }
      {children && <MatExpansionPanelDetails>{children}</MatExpansionPanelDetails>}
    </MatExpansionPanel>
  );
};

ExpansionPanel.defaultProps = {
  defaultExpanded: true,
  disabled: false,
  square: true,
  title: ''
};

ExpansionPanel.displayName = 'ExpansionPanel';

export { ExpansionPanel };
